import * as React from 'react';
import {
    Card,
    CardImage,
    CardBody,
    CardActions,
    CardTitle,
    CardSubtitle
} from '@progress/kendo-react-layout';
import { Button } from '@progress/kendo-react-buttons';
import { Link } from 'react-router-dom';
import { SvgIcon } from '@progress/kendo-react-common';
import * as svgIcons from "@progress/kendo-svg-icons";
import { t } from 'i18next';
import { SearchMissingsService } from 'services/Missings/SearchMissingsService';
import { useNotification } from 'contexts/NotificationContext';
import { Trans } from 'react-i18next';
// Constant for the desired total height of the Card
const CARD_HEIGHT = '500px';
// Image height: 1/2 of the total card height if present
const IMAGE_HEIGHT = '200px';

interface PostCardComponentProps {
    documentId: number;
    documentProviderId: string;
    periodStartDate: string;
    periodEndDate: string;
    imageUrl?: string;
    title?: string;
    source?: string;
    content?: string;
    status?: 'CompleteMissing' | 'NotImported' | 'Imported' | 'NotInPanel';
    url?: string;
    uniqueId?: string;
    publishDate?: Date;
}

const PostCardComponent = (props: PostCardComponentProps) => {
    
    const hasImage = !!props.imageUrl;
    const searchMissingsService = new SearchMissingsService();
    const notification = useNotification();
    const [showPostsLoading, setShowPostsLoading] = React.useState<boolean>(false); 
    const [post, setPost] = React.useState<PostCardComponentProps>(props);

    const getDocumentStatusBadge = (status: string | undefined) => {
        switch (status) {
          case "CompleteMissing":
            return "badge-complete-missing";
          case "NotImported":
            return "badge-not-imported";
          case "Imported":
            return "badge-imported";   
          case "NotInPanel":
            return "badge-not-in-panel";
        }
      }
    const ImportedRefresh = (dictionary: { [key: string]: number }) => {
        if (dictionary.hasOwnProperty(post.uniqueId || '')) {
            const value = dictionary[post.uniqueId || ''];
            setPost({
                ...post,
                documentId: value,
                status: 'Imported',
            });
            // Perform any additional logic here if needed
        }
    };
    // Dynamic height for the CardBody:
    // If there is an image (125px), the body occupies the remaining height.
    // If there is no image, the body occupies the total height (250px), minus the space for CardActions.
    // We use 'auto' to let flexbox handle the space.
    const importDocument = async () => {   
        setShowPostsLoading(true);
        await searchMissingsService
        .ImportMissingDocuments({
            ImportAll: false,
            UniqueIds: post.uniqueId ? [post.uniqueId] : [],
            PeriodStartDate: post.periodStartDate,
            PeriodEndDate: post.periodEndDate
        })
        .then((response: any) => {
            console.log(`Document with Unique id: ${post.uniqueId} imported.`);
            notification.showNotification(`Document with Unique id: ${post.uniqueId} imported.`);

            ImportedRefresh(response);
            setShowPostsLoading(false);
        })
        .catch((error: any) => {
          console.error("Error importing: ", error);
          setShowPostsLoading(false);
        });
    };

    return (
        <div style={{ maxWidth: '350px', margin: '20px auto' }}>
            <Card
                style={{
                    height: CARD_HEIGHT,
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)', // Add gradient background
                    border: '1px solid #d1d5db', // Optional border for better contrast
                    borderRadius: '8px', // Optional rounded corners
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', // Optional shadow for depth
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <span style={{ margin: '8px 8px 0px 0px' }} className={getDocumentStatusBadge(post.status)}>{t("dataLabeling.missings." + post.status)}</span>  
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                    <p style={{ margin: '0px 16px' }}> {post.uniqueId}</p>
                    
                </div>
                {post.url && (
                    <p style={{ margin: '8px 16px' }}>
                        <a 
                            href={post.url?.startsWith('http') ? post.url : `http://${post.url}`} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            style={{ color: 'blue', textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: '100%' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {post.url}
                        </a>
                    </p>
                )}

                {hasImage && (
                    <CardImage
                        src={post.imageUrl}
                        alt="Post Card Image"
                        style={{ height: IMAGE_HEIGHT, objectFit: 'cover' }} // Fixed image height
                    />
                )}
                {post.status === 'CompleteMissing' &&
                    <div
                        style={{
                            height: IMAGE_HEIGHT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#9ca3af',
                        }}
                    >
                        <SvgIcon
                            icon={svgIcons.fileErrorIcon}
                            size="large"
                            style={{ width: '64px', height: '64px' }}
                        />
                    </div>
                }
                <CardBody
                    // Allow cardbody to grow and fill available space
                    style={{ flexGrow: 1, overflow: 'hidden' }}
                >
                    
                    <CardTitle>
                        {post.title}
                    </CardTitle>
                    {post.status !== 'CompleteMissing' &&   
                        <CardSubtitle>
                            {post.publishDate ? new Date(post.publishDate).toLocaleDateString() : ''} 
                        </CardSubtitle>
                    }
                    {/* hidden overflow to avoid problems with too much text */}
                    <p style={{flexGrow: 1, margin: 0, fontSize: '0.85em', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitBoxOrient: 'vertical' }}>
                        {post.content}
                    </p>
                {post.status === 'CompleteMissing' &&
                    <p style={{flexGrow: 1, margin: 0, fontSize: '0.85em', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitBoxOrient: 'vertical' }}>
                        <Trans>dataLabeling.missings.missingContent</Trans>
                    </p>
                }
                </CardBody>

                <CardActions
                    layout={"end"}
                    orientation={"horizontal"} 
                    style={{ padding: '8px 16px', flexShrink: 0, textAlign: 'left' }} // Ensure parent container aligns text to the left
                >
                    {(showPostsLoading &&  
                        <span className="k-icon k-i-loading k-spin" style={{ fontSize: "30px" }}></span>
                    )}
                    {!showPostsLoading && post.status === 'NotImported' &&
                        <Button 
                            onClick={importDocument} 
                            themeColor={"primary"} 
                        >
                            Import
                        </Button>
                    }

                    {post.status === 'Imported' &&
                    <Link
                        to="/dashboard/labeling"
                        onClick={() => {
                            localStorage.setItem("documentId", post.documentId as any);
                            localStorage.setItem("operation", "missing");
                        }}
                        target="_blank"
                        className="k-button mx-2 k-button-sm k-button-outline k-button-outline-secondary k-rounded-sm"
                    >        
                        <span title={t("dataLabeling.search.open")}>
                            <SvgIcon
                                icon={svgIcons.arrowRightIcon}
                                size="large"
                            />
                        </span>
                    </Link>
                }
                {post.status === 'NotInPanel' &&
                    <SvgIcon
                        icon={svgIcons.cancelOutlineIcon}
                        size="large"
                        style={{ width: '36px', height: '36px' }}
                    />
                }

                </CardActions>
            </Card>
        </div>
    );
}

export default PostCardComponent;